import { Box, BoxSx, Text } from "@modernatx-internal/component-library";
import React from "react";

import { BarChartContent, ContentComponentProps } from "../types";

type BarChartProps = ContentComponentProps<BarChartContent>;

const GAP_DEFAULT: BoxSx["gap"] = [2, null, 5];
const GUTTER_DEFAULT: BoxSx["paddingLeft"] = [2, null, 5];

function getIntervalPercentCovered(value: number, intervals: number[]): number {
  if (intervals.length < 2) {
    throw new Error("At least two intervals are required.");
  }

  let i = 0;

  while (i < intervals.length - 1 && value > intervals[i + 1]!) {
    i = i + 1;
  }

  if (value <= intervals[0]!) {
    return 0;
  }
  if (value >= intervals[intervals.length - 1]!) {
    return 100;
  }

  const lower = intervals[i]!;
  const upper = intervals[i + 1]!;
  const intervalFraction = (value - lower) / (upper - lower);
  const stepFraction = (i + intervalFraction) / (intervals.length - 1);

  return stepFraction * 100;
}

const BarChartBar: React.FC<{
  bar: BarChartProps["sections"][0]["groups"][0]["bars"][0];
  chart: BarChartProps;
}> = ({ bar, chart }) => {
  const { label, stacks } = bar;
  const { barsMaxWidth, barsVariant, intervals, height: chartHeight } = chart;
  const intervalValues = React.useMemo(
    () => intervals.map(({ value }) => value).sort((a, b) => a - b),
    [intervals]
  );

  if (barsVariant === "horizontal") {
    return (
      <Box
        sx={{
          display: "flex",
          width: "100%"
        }}
      >
        <Box
          sx={{
            alignItems: "center",
            display: "flex",
            flexDirection: "column",
            justifyContent: "end",
            textAlign: "center",
            width: "100%"
          }}
        >
          <Box
            sx={{
              color: stacks[stacks.length - 1]?.color,
              fontSize: "font-size-3",
              fontWeight: "font-weight-bold",
              whiteSpace: "nowrap"
            }}
          >
            {label}
          </Box>
          <Box
            sx={{
              display: "flex",
              maxWidth: barsMaxWidth ? () => barsMaxWidth * stacks.length : null,
              width: "100%"
            }}
          >
            {stacks.map((stack, key) => {
              const height = getIntervalPercentCovered(stack.value, intervalValues);

              return (
                <Box
                  key={key}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "end",
                    textAlign: "center",
                    width: "100%"
                  }}
                >
                  {stack.label && (
                    <Box
                      sx={{
                        color: stack.color,
                        fontSize: "font-size-3",
                        fontWeight: "font-weight-bold",
                        whiteSpace: "nowrap"
                      }}
                    >
                      {stack.label}
                    </Box>
                  )}
                  <Box
                    sx={{
                      // @ts-ignore
                      height: ({ sizes }) => `${((sizes[chartHeight] || 0) * height) / 100}px`,
                      display: "flex",
                      flexShrink: 0,
                      justifyContent: "center",
                      minHeight: () => (height > 0 ? "1px" : null),
                      transition: () => "height 300ms cubic-bezier(0.4, 0.005, 0.06, 1)",
                      width: "100%"
                    }}
                  >
                    <Box
                      sx={{
                        backgroundColor: stack.color,
                        borderBottom: "unset",
                        width: "100%",
                        ...stack.sx
                      }}
                    />
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        textAlign: "center",
        width: "100%"
      }}
    >
      <Box
        sx={{
          color: stacks[stacks.length - 1]?.color,
          fontSize: "font-size-3",
          fontWeight: "font-weight-bold",
          whiteSpace: "nowrap"
        }}
      >
        {label}
      </Box>
      {stacks.map(({ color, sx, value }, k) => {
        const height = getIntervalPercentCovered(value, intervalValues);

        return (
          <Box
            key={k}
            sx={{
              height: () => `${height}%`,
              display: "flex",
              flexShrink: 0,
              justifyContent: "center",
              transition: () => "height 300ms cubic-bezier(0.4, 0.005, 0.06, 1)",
              width: "100%"
            }}
          >
            <Box
              sx={{
                backgroundColor: color,
                borderBottom: "unset",
                maxWidth: barsMaxWidth,
                width: "100%",
                ...sx
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

const BarChartSectionDescriptor: React.FC<{
  chart: BarChartProps;
  section: BarChartProps["sections"][0];
}> = ({ chart, section }) => {
  const { sectionGutter = GUTTER_DEFAULT } = chart;
  const { range, title } = section;

  const renderLineAcross = React.useCallback(
    () => (
      <Box sx={{ display: "flex", flex: 1, flexDirection: "column", justifyContent: "center" }}>
        <Box sx={{ height: () => "1px", backgroundColor: "black" }} />
      </Box>
    ),
    []
  );

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        pb: !range && !title ? null : 6,
        px: sectionGutter,
        width: "100%"
      }}
    >
      {title && (
        <Text
          as="div"
          sx={{
            backgroundColor: "color-system-gray-100",
            borderRadius: "radius-round",
            px: 5,
            py: 2,
            textAlign: "center"
          }}
          variant="body-1-bold"
        >
          {title}
        </Text>
      )}
      {range && (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            justifyContent: "center"
          }}
        >
          {renderLineAcross()}
          <Text variant="body-1-bold" sx={{ flexGrow: 0 }}>
            {range}
          </Text>
          {renderLineAcross()}
        </Box>
      )}
    </Box>
  );
};

const BarChartSectionDescriptorTitle: React.FC<BarChartProps> = ({ labelRange }) => {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "end",
        pb: !labelRange ? null : 6,
        width: "100%"
      }}
    >
      <Box
        sx={{
          alignItems: "end",
          display: "flex",
          flexDirection: "column",
          justifyContent: "end"
        }}
      >
        <Text variant="body-1-bold" sx={{ flexGrow: 0 }}>
          {labelRange}
        </Text>
      </Box>
    </Box>
  );
};

const BarChartSectionXAxis: React.FC<{
  chart: BarChartProps;
  section: BarChartProps["sections"][0];
}> = ({ chart, section }) => {
  const { sectionGap = GAP_DEFAULT, sectionGutter = GUTTER_DEFAULT } = chart;
  const { groups } = section;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column"
      }}
    >
      <Box
        sx={{
          display: "flex",
          gap: sectionGap,
          justifyContent: "center",
          px: sectionGutter,
          width: "100%"
        }}
      >
        {groups.map((group, i) => (
          <React.Fragment key={i}>
            {group.bars.map((bar, j) => (
              <Box
                key={j}
                sx={{
                  alignItems: "center",
                  display: "flex",
                  flexDirection: "column",
                  fontWeight: "font-weight-bold",
                  gap: 2,
                  width: "100%"
                }}
              >
                <Box
                  sx={{
                    backgroundColor: "black",
                    height: 1,
                    width: 1
                  }}
                />
                {bar.title}
              </Box>
            ))}
          </React.Fragment>
        ))}
      </Box>
      <Box
        sx={{
          display: "flex",
          gap: sectionGap,
          justifyContent: "center",
          px: sectionGutter,
          width: "100%"
        }}
      >
        {groups.map((group, i) => (
          <Box key={i} sx={{ textAlign: "center", width: "100%" }}>
            {group.label ? <Box sx={{ pt: 5 }}>{group.label}</Box> : null}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

const BarChartSectionBars: React.FC<{
  chart: BarChartProps;
  first: boolean;
  section: BarChartProps["sections"][0];
}> = ({ chart, first, section }) => {
  const { height, sectionGap = GAP_DEFAULT, sectionGutter = GUTTER_DEFAULT } = chart;
  const { groups, label } = section;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "100%"
      }}
    >
      <Box
        sx={{
          backgroundColor: "surface-neutral",
          display: "flex",
          height,
          position: "relative",
          width: "100%"
        }}
      >
        <Text
          as="div"
          sx={{
            position: "absolute",
            px: sectionGutter,
            width: "100%",
            top: 0,
            textAlign: "center",
            left: 0
          }}
          variant="body-1-bold"
        >
          {label}
        </Text>
        <Box
          sx={{
            borderBottom: "3px solid",
            borderBottomStyle: "solid",
            borderColor: "black",
            borderLeft: first ? "3px solid" : null,
            display: "flex",
            gap: sectionGap,
            justifyContent: "center",
            px: sectionGutter,
            width: "100%"
          }}
        >
          {groups.map((group, i) => (
            <React.Fragment key={i}>
              {group.bars.map((bar, j) => (
                <BarChartBar key={j} bar={bar} chart={chart} />
              ))}
            </React.Fragment>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

const BarChartYAxis: React.FC<BarChartProps> = ({ height, labelYAxis, intervals }) => {
  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "end",
        gap: 3
      }}
    >
      {labelYAxis && (
        <Text
          sx={{ display: "flex", flexDirection: "column", justifyContent: "center" }}
          variant="body-1-bold"
        >
          {labelYAxis}
        </Text>
      )}
      <Box
        sx={{
          alignItems: "end",
          display: "flex",
          flexDirection: "column",
          height,
          justifyContent: "space-between",
          position: "relative"
        }}
      >
        {intervals.map((interval, i) => {
          const percentStretch = (i - intervals.length / 2) / (intervals.length / 2);
          const pxAdjustment = i === 0 ? 2 : i === intervals.length - 1 ? 1.5 : 0;

          return (
            <Box
              key={i}
              sx={{
                alignItems: "center",
                display: "flex",
                fontSize: "font-size-3",
                fontWeight: "font-weight-bold",
                gap: 2,
                textAlign: "end",
                transform: `translateY(calc(${50 * percentStretch}% + ${pxAdjustment}px))`
              }}
            >
              {interval.label}
              <Box
                sx={{
                  backgroundColor: "black",
                  height: 1,
                  width: 1
                }}
              />
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export const BarChart: React.FC<BarChartProps> = (props) => {
  const { sections } = props;
  const groupColumnsStyle = React.useMemo(() => {
    return new Array(sections.length)
      .fill(null)
      .map(() => " 1fr 2px")
      .join("");
  }, [sections.length]);

  return (
    <Box
      sx={{
        alignItems: "stretch",
        display: "grid",
        gridAutoFlow: "column",
        gridTemplateRows: `repeat(3, auto)`,
        gridTemplateColumns: `max-content${groupColumnsStyle}`,
        width: "100%"
      }}
    >
      {/* Column 1 */}
      <BarChartSectionDescriptorTitle {...props} />
      <BarChartYAxis {...props} />
      <Box />
      {sections.map((section, i) => (
        <React.Fragment key={i}>
          {/* Column 2 */}
          {i !== 0 && (
            <>
              <Box
                sx={{
                  borderBottom: "3px solid",
                  borderColor: "black",
                  gridRow: "span 2",
                  height: "100%",
                  overflow: "visible",
                  position: "relative"
                }}
              >
                <Box
                  sx={{
                    borderImage: `repeating-linear-gradient(to bottom, black 0, black 10px, transparent 8px, transparent 22px)`,
                    borderImageSlice: 1,
                    borderLeft: `2px solid transparent`,
                    height: "100%",
                    left: 0,
                    position: "absolute",
                    top: 0,
                    width: 0
                  }}
                />
              </Box>
              <Box />
            </>
          )}
          {/* Column 3 */}
          <BarChartSectionDescriptor chart={props} section={section} />
          <BarChartSectionBars chart={props} first={i === 0} section={section} />
          <BarChartSectionXAxis chart={props} section={section} />
        </React.Fragment>
      ))}
    </Box>
  );
};
